import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  setSecureToken, 
  getSecureToken, 
  removeSecureToken, 
  isTokenValid,
  incrementFailedAttempts,
  resetFailedAttempts,
  detectSuspiciousActivity
} from '../utils/security';

export interface User {
  id: number;
  login: string;
  type: 'admin' | 'manager' | 'client' | 'salary';
  category: number | null;
  /** Bitrix city IDs for `salary` users (report access scope). */
  salaryCategories?: number[];
  sources: Array<{
    id: string;
    name: string;
    code: string;
  }>;
  ukNames: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Проверяем сохраненный токен при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      const token = getSecureToken();
      if (token && isTokenValid(token)) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          // Валидируем токен через запрос к API
          const response = await api.get('/api/v1/auth/profile');
          setUser(response.data);
          resetFailedAttempts(); // Сбрасываем счетчик неудачных попыток
        } catch (error) {
          // Токен недействителен
          removeSecureToken();
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
        }
      } else if (token) {
        // Токен истек
        removeSecureToken();
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (login: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      // Проверяем на подозрительную активность
      if (detectSuspiciousActivity()) {
        setError('Слишком много неудачных попыток входа. Попробуйте позже.');
        return; // Не выбрасываем ошибку
      }
      
      const response = await api.post('/api/v1/auth/login', { login, password });
      const { access_token, user: userData } = response.data;
      
      // Сохраняем токен безопасно
      setSecureToken(access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      resetFailedAttempts(); // Сбрасываем счетчик неудачных попыток
      
      // Перенаправляем в зависимости от роли пользователя
      if (userData.type === 'admin') {
        navigate('/admin');
      } else if (userData.type === 'manager') {
        navigate('/manager');
      } else if (userData.type === 'client') {
        navigate('/client');
      } else if (userData.type === 'salary') {
        navigate('/report');
      } else {
        navigate('/'); // fallback на дашборд
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Ошибка входа';
      setError(errorMessage);
      incrementFailedAttempts(); // Увеличиваем счетчик неудачных попыток
      // Не выбрасываем ошибку, чтобы избежать неожиданного поведения
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    removeSecureToken();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
